import { Injectable, computed, inject, signal } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { LanguageService } from "../language.service";
import { GameProviderType } from '../../types/game.type';

@Injectable({
  providedIn: 'root'
})
export class GameProvidersService {

  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _lang = inject(LanguageService);

  public list = signal<GameProviderType[]>([]);
  public listInFilter = computed(() => {
    return this.list()?.filter(e => e.inFilter);
  });
  public listInFooter = computed(() => {
    return this.list()?.filter(e => e.inFooter);
  });

  /**
   * Game provider list observable
   */
  public list$: ReplaySubject<GameProviderType[]> = new ReplaySubject<GameProviderType[]>(1);

  /**
   * Subscribes to language change events and reloads the provider list
   * when the language changes, ensuring the list is updated with localized data
   */
  public updateProviderListOnLangChange() {
    this._lang.langChange$.subscribe(() => {
      this.loadProviderList();
    });
  }

  /**
   * Loads the game provider list from the CMS API
   *
   * Retrieves the list of game providers, optionally filtering by user's country if authenticated.
   * Transforms the provider data by converting aliases to lowercase and sorting providers by name.
   * Updates the internal list of providers and filter providers list, and emits the updated list.
   */
  public loadProviderList() {
    let params = {};
    if (this._user.auth) {
      params = {
        country: this._user.info.country
      };
    }
    this._api.gameProviderList(params).pipe(
      first(),
      map(response => {
        return (response.data || []).map(provider => ({
          ...provider,
          alias: provider?.alias?.toLowerCase()
        })).sort((a, b) => {
          if (a.name < b.name) { return -1; }
          if (a.name > b.name) { return 1; }
          return 0;
        });
      }),
      tap(list => {
        this.list.set(list);
        this.list$.next(list);
      })
    ).subscribe();
  }

  /**
   * Returns provider by id
   *
   * @param id
   */
  getById(id: number): GameProviderType {
    return this.list()?.find(provider => provider?.id?.toString() === id?.toString());
  }

  /**
   * Returns provider by name
   *
   * @param alias
   */
  getByAlias(alias: string): GameProviderType {
    alias = alias?.toLowerCase()?.replace(' ', '-');
    return this.list()?.find(provider => provider?.alias?.toLowerCase() === alias);
  }

}
